import * as yup from 'yup';

import { cardAttributes } from '~/constants';
import { CardTypes, CardTypesValues } from '~/constants/general';
import { ActionCardConfig } from '~/containers/Conversations/Cards/ActionCard/types';
import {
  COMMA_LIST_DELIMITER,
  STRING_LIST_DELIMITER,
} from '~/containers/Conversations/Cards/constants';
import { FormAICardParam4Schema } from '~/containers/Conversations/Cards/FormAICard/utils/FormAICardParam4Schema';
import { ConversationDataR } from '~/redux/reducers/types';

import { isValidAttributeName } from './attributeUtils';
import { parseInstaStackActionBody } from './instaStackUtils';

export function yupErrorToObject<
  T extends Record<string, string> = Record<string, string>,
>(e: unknown): T {
  if (!(e instanceof yup.ValidationError)) return {} as T;

  return e.inner.reduce((acc, error) => {
    if (error.path) (acc as any)[error.path] = error.message;
    return acc;
  }, {} as T);
}

const AttributeCardSchema = yup.object({
  param1: yup.string().required('Required'),
  param2: yup.string().required('Required').nullable(),
  param3: yup
    .string()
    .oneOf(['true', 'false'], 'Clear value must be true or false')
    .notRequired(),
});

export const validateCard = (
  card: ConversationDataR['intentCardsData'][number]
) => {
  switch (card.action_type) {
    case CardTypes.DATA_SUBMIT: {
      switch (true) {
        case !card.param1?.trim():
          return {
            valid: false,
            text: cardAttributes[card.action_type].errors.blankText,
          };
        default:
          return {
            valid: true,
            text: '',
          };
      }
    }
    case CardTypes.VIDEO:
    case CardTypes.IMAGE: {
      if (card.param1) return { valid: true, text: '' };
      return {
        valid: false,
        text: cardAttributes[card.action_type].errors.noUrl,
      };
    }
    case CardTypes.QUICK_REPLY:
    case CardTypes.SMS:
    case CardTypes.TEXT: {
      let valid;
      if (!card.param1 || card.param1.trim().length === 0) {
        valid = false;
        return {
          valid,
          text: cardAttributes[card.action_type].errors.blankText,
        };
      }
      return { valid: true, text: '' };
    }
    case CardTypes.CAROUSEL: {
      const carousels = card.intent_carousel ?? [];
      const valid = carousels.every((c) => c.h_choose_title && c.image_url);

      let text: Record<string, { title?: string; image?: string }> = {};

      carousels.forEach((c, idx) => {
        const cardType = card.action_type as CardTypesValues;

        if (!c.h_choose_title || !c.image_url) {
          text = {
            ...text,
            [idx]: {
              title: !c.h_choose_title
                ? (cardAttributes[cardType].errors.noHeading as string)
                : '',
              image: !c.image_url
                ? (cardAttributes[cardType].errors.noImageUrl as string)
                : '',
            },
          };
        }
      });
      return { valid, text };
    }
    case CardTypes.ATTRIBUTE: {
      try {
        AttributeCardSchema.validateSync(card, {
          stripUnknown: true,
          abortEarly: false,
        });

        return { valid: true, text: '' };
      } catch (e) {
        const errors = yupErrorToObject(e);
        return {
          valid: false,
          text: Object.keys(errors).length
            ? errors
            : { param1: 'Validation error' },
        };
      }
    }
    case CardTypes.GOTO: {
      if (!card?.intent_choices?.length) {
        return { valid: false, text: [0] };
      }

      const invalidIndexes = card.intent_choices
        ?.map((choice, index) => {
          if (choice.action_data) return null;
          return index;
        })
        .filter(Number.isInteger);

      return { valid: !invalidIndexes?.length, text: invalidIndexes };
    }
    case CardTypes.GOAL: {
      let valid;
      if (!card.param1 || card.param1.trim().length === 0) {
        valid = false;
        return {
          valid,
          text: cardAttributes[card.action_type].errors.blankText,
        };
      }
      return { valid: true, text: '' };
    }
    case CardTypes.FALLBACK: {
      let valid;
      if (isWhiteSpaceString(card.param1 || '')) {
        valid = false;
        return {
          valid,
          text: cardAttributes[card.action_type].errors.blankText,
        };
      }
      return { valid: true, text: '' };
    }
    case CardTypes.INSTA_STACK_API: {
      const data = parseInstaStackActionBody(card?.api_action?.api_action_body);

      const valid =
        Boolean(data?.query) &&
        Boolean(data?.collection_name) &&
        Boolean(data?.prompt);

      return {
        valid,
        text: {
          prompt: !data?.prompt ? 'Prompt cannot be blank' : '',
          query: !data?.query ? 'Query cannot be blank' : '',
          collection: !data?.collection_name
            ? 'Collection cannot be blank'
            : '',
        },
      };
    }
    case CardTypes.SMS_VALIDATION: {
      const valid = Boolean(card.param1) && card.intent_choices?.length === 4;

      return {
        valid,
        text: 'Please fill all fields',
      };
    }
    case CardTypes.INTENT_AI: {
      const validIntents =
        card.param2 &&
        card.param2.split(COMMA_LIST_DELIMITER).filter(Boolean).length >= 3;

      const valid = Boolean(validIntents && card.param2);

      return {
        valid,
        text: {
          intentsError: !validIntents
            ? 'At least 3 intents must be specified'
            : '',
          fallbackIntentError: !card.param3
            ? 'Please add a fallback intent'
            : '',
        },
      };
    }
    case CardTypes.FORM_AI: {
      const validFields =
        card.param1 && card.param1.split(COMMA_LIST_DELIMITER).some(Boolean);
      const validName = Boolean(card.param2);
      const validAttributeName = card.param1
        ? card.param1
            .split(COMMA_LIST_DELIMITER)
            .map((attr) =>
              isValidAttributeName(attr)
                ? ''
                : 'can only contain lowercase letters, numbers, or underscores '
            )
        : [];

      let param4Errors: Partial<Record<keyof FormAICardParam4Schema, string>> =
        {};
      try {
        FormAICardParam4Schema.validateSync(card.param4, { abortEarly: false });
      } catch (e) {
        const errors =
          yupErrorToObject<Record<keyof FormAICardParam4Schema, string>>(e);
        param4Errors = { ...param4Errors, ...errors };
      }

      return {
        valid: Boolean(
          validName && validFields && Object.keys(param4Errors).length === 0
        ),
        text: {
          nameError: !validName ? 'Required' : '',
          fieldsError: !validFields ? 'At least 1 field must be specified' : '',
          validAttributeName,
          ...param4Errors,
        },
      };
    }
    case CardTypes.ACTION_CARD: {
      const cardConfig = ActionCardConfig.validateSync(
        card.action_card?.action_config_json
      );

      if (!cardConfig) {
        return {
          valid: false,
          text: 'Invalid action card configuration',
        };
      }

      const { customerAttribute, ...params } = cardConfig;

      const validCustomAttribute =
        !customerAttribute?.required || Boolean(card.cust_attr_id);

      const paramsWithError = Object.entries(params)
        .filter(([_key, param]) => param.show)
        .reduce((acc, [key, param]) => {
          if (!param.required) return acc;

          const value = (card[key as keyof typeof card] ?? '') as string;
          return value?.trim() ? acc : { ...acc, [key]: 'Required' };
        }, {});

      return {
        valid: Boolean(
          validCustomAttribute && Object.keys(paramsWithError).length === 0
        ),
        text: {
          customAttributeError: !validCustomAttribute ? 'Required' : '',
          ...paramsWithError,
        },
      };
    }
    case CardTypes.IMAGE_UPLOAD: {
      const validName = !isWhiteSpaceString(card.param1 || '');
      const hasSuccessBlock = Boolean(card.param2);
      const hasErrorBlock = Boolean(card.param3);

      const valid = Boolean(validName && hasSuccessBlock && hasErrorBlock);
      return {
        valid,
        text: {
          name: validName ? '' : 'Required',
          successBlock: hasSuccessBlock ? '' : 'Required',
          errorBlock: hasErrorBlock ? '' : 'Required',
        },
      };
    }
    case CardTypes.IMAGE_DATA_EXTRACT: {
      const validFields =
        card.param1 && card.param1.split(STRING_LIST_DELIMITER).some(Boolean);
      const isValidUrl = validURL(card.param2 ?? '');
      const param2 = card.param2 ?? '';
      const isAttributeSyntax = param2.startsWith('${') && param2.endsWith('}');
      const attributeName = isAttributeSyntax ? param2.slice(2, -1) : param2;
      const isValidAttribute =
        isAttributeSyntax && isValidAttributeName(attributeName);

      const isValidAttributeOrUrl = isValidAttribute || isValidUrl;

      return {
        valid: Boolean(card.param2 && isValidAttributeOrUrl && validFields),
        text: {
          imageUrlError: card.param2
            ? isValidAttribute || isValidUrl
              ? ''
              : 'Must be a valid url or attribute name'
            : 'Required',
          fieldsError: !validFields ? 'At least 1 field must be specified' : '',
        },
      };
    }
    case CardTypes.RESET_FORM_AI: {
      const validAttributes = yup.string().required().isValidSync(card.param1);
      const validForm = yup.string().required().isValidSync(card.param2);

      return {
        valid: Boolean(validAttributes && validForm),
        text: {
          attributesError: !validAttributes
            ? 'List of attributes is required'
            : '',
          formError: !validForm ? 'Required' : '',
        },
      };
    }
    default:
      return { valid: true, text: '' };
  }
};

export const validURL = (url: string) => {
  try {
    new URL(url);
    return true;
  } catch (e) {
    return false;
  }
};

export const phoneRegex =
  /^(\+)+([0-9]\d{9}|[0-9]\d{10}|[0-9]\d{11}|[0-9]\d{12}|[0-9]\d{13}|[0-9]\d{14})$/gm;

export const validateEmail = (email: string) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return !!re.test(email);
};

export const isWhiteSpaceString = (str: string) => !/\S/.test(str);

export const isNullOrUndefined = (value: unknown): value is null | undefined =>
  value === null || typeof value === 'undefined';

export const isValidNewOption = (
  inputValue: string,
  selectOptions: Array<{ label: string; value: string | number }>
) => {
  const isNotDuplicated = !selectOptions.find(
    (option) => option.label === inputValue
  );

  const isNotEmpty = !isWhiteSpaceString(inputValue);

  return isNotDuplicated && isNotEmpty;
};
